import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import b from 'b_';
import cx from 'classnames';
import { Chevrone as IconChevrone } from 'education-icons';
import isNil from 'lodash/isNil';
import { Button, Image, Help, Text, Link, ImageContextProvider } from 'platform-components';
import { SCREEN_RESOLUTIONS } from 'platform-components/constants';
import { getStickersConfig } from 'platform-components/src/components/app-factory/selectors';
import { ImagesMenu } from 'platform-components/src/components/images-menu/images-menu';
import { InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import { getStudentDisplayName, downloadStudentCert } from 'platform-components/utils';
import React, { useState } from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import type { CertificatesStore } from 'common.components/certificates/typings';
import { getStudents } from 'common.components/classes/selectors';
import { ClassesState } from 'common.components/classes/typings';
import { SCROLL_CONTROLS_MODE, SCROLL_DIRECTION, useScrollTableEffect } from 'common.components/hooks';
import { lessonContentActions } from 'common.components/lesson-content/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { LessonStore } from 'common.components/lesson/typings';
import { isLessonCompleted } from 'common.components/lesson/utils';
import ResultsCellDescription from 'common.components/results/__cell-description/results__cell-description';
import StudentLink from 'common.components/results/__student-link/results__student-link';
import { ResultsTableBlockBody } from 'common.components/results/__table-block-body/results__table-block-body';
import withTime, { type InjectedProps as WithNowProps } from 'common.components/timer/withTime';
import { ReduxUserStore } from 'common.components/user/typings';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import { getClessonMetaStickers, getClessonStudentsStickers, getStickerResources } from 'store/stickers/selectors';
import type { ReduxStickersStore } from 'store/stickers/typings';
import { getResourceBySlug } from 'store/stickers/utils/get-resource-by-slug';
import {
    buildCertFilename,
    buildFallbalckCertFilename,
    downloadFile,
    useOnPdfGenerationError,
} from 'utils/download-file';
import { getFormattedTime } from 'utils/get-formatted-time';
import { getTeamIdFromPath } from 'utils/path';

import { PdfGeneratorLink } from '@edu-frontend/modules/src/features/pdfGenerator';
import ResultsControls from './__controls/results__controls';
import DictationResult from './__dictation/results__dictation';
import { ResultsHeaderCardCell } from './__header-card-cell/results__header-card-cell';
import { ResultHeaderCellDescription } from './__header-cell-description/results__header-cell-description';
import TableBlock from './__table-block/results__table-block';
import TableBody from './__table-body/results__table-body';
import TableCell from './__table-cell/results__table-cell';
import TableHead from './__table-head/results__table-head';
import TableRow from './__table-row/results__table-row';
import Table from './__table/results__table';
import { resultsActions } from './actions';
//@ts-ignore
import cssConstants from './common-constants.scss';
import { useShowStickersPopup } from './hooks/useShowStickersPopup';
import * as selectors from './selectors';
import { getCellValue, getSticker, getStubValue, helpProps } from './utils';

import './results.scss';

const problemCellWidth = parseInt(cssConstants.problemCellWidth, 10);
const problemCellMargin = parseInt(cssConstants.problemCellSideMargin, 10);
const problemCellTotalWidth = problemCellWidth + problemCellMargin;
const SCROLL_STEP = 5;

interface HOCProps extends WithRouterPathProps, WrappedComponentProps, WithNowProps {}

interface OwnProps {
    mix?: string;
    onHeaderCardClick: (problemId: number) => void;
    onStudentNameClick: (studentId: number) => void;
    onAssignClessonStudentsSticker?: (args: { studentIds: number[]; slug: string }) => void;
    onCancelClessonStudentsSticker?: (args: { reactionIds: string[]; studentIds: number[] }) => void;
    showHelp?: boolean;
}

type StoreProps = ReturnType<typeof mapStateToProps>;

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface Props extends OwnProps, HOCProps, StoreProps, DispatchProps {}

// eslint-disable-next-line complexity
const Results = (props: Props) => {
    const {
        lessonLoadingStatus,
        canShowProblemsContent,
        problemsResult,
        intl,
        students,
        studentsResult,
        certificates,
        isStatisticsAvailable,
        lessonHasCertificates,
        mix,
        onHeaderCardClick,
        onStudentNameClick,
        onAssignClessonStudentsSticker,
        onCancelClessonStudentsSticker,
        problems,
        dictationProblems,
        isEvaluated,
        isStatsExpanded,
        isPointsMode,
        isDefaultMode,
        maxPoints,
        isLessonScoreAvailable,
        showHelp = true,
        shouldShowSeenCountColumnStats,
        shouldShowTimeColumnStats,
        clessonMetaStickers,
        clessonStudentsStickers,
        isClessonMetaHasStickers,
        stickerResources,
        stickersConfig,
        studentIdsWithoutResult,
        clessonId,
        expandProblem,
        gameCertSubject,
        classId,
    } = props;

    const {
        scrollableContainerRef: tableBlockRef,
        scrollControlsMode,
        onControlClick,
        getCellsOffset,
        containerWidth,
    } = useScrollTableEffect({
        entitiesToScroll: problems,
        cellWidth: problemCellWidth,
        cellTotalWidth: problemCellTotalWidth,
        scrollStep: SCROLL_STEP,
        shouldProcess: !isStatsExpanded,
    });

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [cellRef, setCellRef] = useState<HTMLDivElement | null>(null);
    const [headerCardRef, setHeaderCardRef] = useState<HTMLDivElement | null>(null);
    const cardRefs = React.useRef<Array<HTMLDivElement | null>>([]);

    const resultRef = React.useRef<HTMLDivElement | null>(null);

    const studentStickerRefs = React.useRef<Array<HTMLElement | null>>([]);
    const [checkedStickerCellIndex, setCheckedStickerCellIndex] = useState<number | null>(null);

    const { stickersPopupState, showStickersPopupForCheckbox, showStickersPopupForCell, closeStickersPopup } =
        useShowStickersPopup({
            assignClessonStudentsSticker: onAssignClessonStudentsSticker,
            onClosePopup: () => setCheckedStickerCellIndex(null),
            studentIdsWithoutResult,
        });
    const getResourceByStudent = (studentId: number) => {
        const reaction = clessonStudentsStickers[studentId];
        if (reaction && stickersConfig) {
            return getResourceBySlug(reaction.payload.slug, stickersConfig, stickerResources);
        }
        return null;
    };
    const shouldShowStickersColumnStats = isClessonMetaHasStickers;
    const shouldRenderStickersMenu = isClessonMetaHasStickers;

    const handleStickerCellCLick = (studentId: number, cellIndex: number) => {
        const sticker = clessonStudentsStickers[studentId];
        const current = studentStickerRefs.current[cellIndex];
        if (!sticker && shouldRenderStickersMenu && current) {
            setCheckedStickerCellIndex(cellIndex);
            showStickersPopupForCell({ current }, studentId);
        }
        if (sticker && onCancelClessonStudentsSticker) {
            onCancelClessonStudentsSticker({
                reactionIds: [sticker.id],
                studentIds: [studentId],
            });

            ym('reachGoal', 'teacher_lesson_stats_delete_sticker', {
                clesson_id: clessonId,
                student_id: studentId,
                sticker_slug: sticker.payload.slug,
            });
        }
    };

    const cancelAllStickers = onCancelClessonStudentsSticker
        ? () => {
              const reactionIds: string[] = [];
              const studentIds: number[] = [];
              Object.entries(clessonStudentsStickers).forEach(([studentId, sticker]) => {
                  if (sticker) {
                      reactionIds.push(sticker.id);
                  }
                  studentIds.push(Number(studentId));
              });
              onCancelClessonStudentsSticker({
                  reactionIds,
                  studentIds,
              });
          }
        : undefined;

    const renderProblemCell = (problemLinkId: number, index: number) => {
        const isDifficult = Boolean(problemsResult?.[problemLinkId].details.isDifficult);
        const disabled = !canShowProblemsContent;

        return (
            <ResultsHeaderCardCell
                disabled={disabled}
                index={index}
                isDifficult={isDifficult}
                onClick={onHeaderCardClick}
                problemLinkId={problemLinkId}
            />
        );
    };

    const pdfCertFilenames: { [studentId in number]?: string | undefined } = {};

    if (students) {
        for (const s of students) {
            pdfCertFilenames[s.id] = buildCertFilename(intl, s, '.pdf');
        }
    }

    const downloadCertificate = async (args: { studentId: number }) => {
        const { studentId } = args;
        const student = students?.find((s) => s.id === studentId);

        const certificateUrl = certificates?.[studentId].certificate || null;

        if (!student || !certificateUrl) {
            return;
        }

        try {
            await downloadFile(certificateUrl, buildCertFilename(intl, student));
        } catch (err) {
            window.open(certificateUrl, '_blank');
        }
    };

    const shouldShowGameCertificate = Boolean(gameCertSubject);

    const downloadGameCertificate = async (args: { studentId: number }) => {
        const { studentId } = args;
        const student = students?.find((s) => s.id === studentId);
        const studentResult = studentsResult.find((s) => s.studentId === studentId);
        const hasResult = studentResult?.result.overall.time;

        if (!student || !hasResult || !gameCertSubject) {
            return;
        }

        try {
            const fileName = intl.formatMessage(
                { id: 'studentGameCertificate.fileName' },
                {
                    student: getStudentDisplayName(student),
                }
            );

            await downloadStudentCert(studentId, gameCertSubject, fileName);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCardCellMouseEnter = (index: number): void => {
        setHoveredIndex(index);

        if (!canShowProblemsContent && cardRefs && cardRefs.current[index]) {
            setHeaderCardRef(cardRefs.current[index]);
        }
    };

    const handleCardCellMouseLeave = (): void => {
        setHoveredIndex(null);
        setHeaderCardRef(null);
    };

    const onPdfGenerationError = useOnPdfGenerationError();

    /* eslint-disable complexity */
    const renderProblemStats = (): React.ReactNode => {
        return (
            <>
                <ResultsControls
                    cancelAllStickers={shouldRenderStickersMenu ? cancelAllStickers : undefined}
                    scrollControlsMode={scrollControlsMode}
                    showStickersPopup={shouldRenderStickersMenu ? showStickersPopupForCheckbox : undefined}
                />
                <Table>
                    <TableHead>
                        <TableCell type={TableCell.TYPE.COUNTER}>
                            <Text id="lessonView.results.counter" />
                        </TableCell>
                        <TableCell options={{ beforeScroll: shouldShowLeftScrollControl }} type={TableCell.TYPE.NAME}>
                            <Text id="lessonView.results.studentName" />
                        </TableCell>
                        {shouldShowLeftScrollControl && (
                            <TableCell
                                onClick={() => {
                                    onControlClick(SCROLL_DIRECTION.LEFT);
                                }}
                                type={TableCell.TYPE.SCROLL_CONTROL}
                            >
                                <div className="results__problem-cell">
                                    <IconChevrone
                                        className={b('results', 'scroll-icon', {
                                            direction: SCROLL_DIRECTION.LEFT,
                                        })}
                                    />
                                </div>
                            </TableCell>
                        )}
                        {!isNil(hoveredIndex) && (
                            <div
                                className="results__table-block-hover"
                                style={{
                                    left: `${hoveredIndex * problemCellTotalWidth - cellOffset}px`,
                                }}
                            />
                        )}
                        <TableBlock
                            header={true}
                            onMouseLeave={handleCardCellMouseLeave}
                            ref={tableBlockRef}
                            style={tableStyle}
                        >
                            {problems.map((problem, index) => (
                                <TableCell
                                    innerRef={(element: HTMLDivElement) => {
                                        cardRefs.current[index] = element;
                                    }}
                                    key={`problem-result-${problem.id}`}
                                    onMouseEnter={() => handleCardCellMouseEnter(index)}
                                    style={style}
                                    type={TableCell.TYPE.CARD}
                                >
                                    {renderProblemCell(Number(problem.id), index)}
                                </TableCell>
                            ))}
                        </TableBlock>
                        {shouldShowRightScrollControl && (
                            <TableCell
                                onClick={() => {
                                    onControlClick(SCROLL_DIRECTION.RIGHT);
                                }}
                                type={TableCell.TYPE.SCROLL_CONTROL}
                            >
                                <div className="results__problem-cell">
                                    <IconChevrone
                                        className={b('results', 'scroll-icon', { direction: SCROLL_DIRECTION.RIGHT })}
                                    />
                                </div>
                            </TableCell>
                        )}
                        {shouldShowSeenColumn && (
                            <TableCell mix="qa_results-seen-overall" type={TableCell.TYPE.OVERALL}>
                                <Text id="lessonView.results.seenNumber" size={Text.SIZE.S} />
                            </TableCell>
                        )}
                        {isDefaultMode && (
                            <TableCell mix="qa_results-success-overall" type={TableCell.TYPE.OVERALL}>
                                <Text id="lessonView.results.successNumber" size={Text.SIZE.S} />
                            </TableCell>
                        )}
                        {isPointsMode && (
                            <TableCell mix="qa_results-points-overall" type={TableCell.TYPE.OVERALL}>
                                <Text
                                    id="lessonView.results.points"
                                    size={Text.SIZE.S}
                                    values={{ maxPoints: maxPoints }}
                                />
                            </TableCell>
                        )}
                        {shouldShowTimeColumnStats && (
                            <TableCell mix="qa_results-time-overall" type={TableCell.TYPE.OVERALL}>
                                <Text id="lessonView.results.time" size={Text.SIZE.S} />
                                {showHelp && (
                                    <Help {...helpProps}>
                                        <Text id="lessonView.results.help" type={Text.TYPE.MARKDOWN} />
                                    </Help>
                                )}
                            </TableCell>
                        )}
                        {shouldShowGameCertificate && (
                            <TableCell mix="qa_results-game-certificate-overall" type={TableCell.TYPE.CERTIFICATE}>
                                <Text id="lessonView.results.gameCertificate" size={Text.SIZE.S} />
                            </TableCell>
                        )}
                        {shouldShowCertificateColumn && (
                            <TableCell mix="qa_results-certificate-overall" type={TableCell.TYPE.CERTIFICATE}>
                                <Text id="lessonView.results.certificate" size={Text.SIZE.S} />
                            </TableCell>
                        )}
                        {shouldShowStickersColumnStats && (
                            <TableCell type={TableCell.TYPE.STICKERS}>
                                <Text id="lessonView.results.stickers" size={Text.SIZE.S} />
                            </TableCell>
                        )}
                    </TableHead>
                    <TableBody>
                        {studentsResult.map((studentResult, index) => (
                            <TableRow key={`student-result-${studentResult.studentId}`}>
                                <TableCell type={TableCell.TYPE.COUNTER}>
                                    <Text>{index + 1}</Text>
                                </TableCell>
                                <TableCell type={TableCell.TYPE.NAME}>
                                    <StudentLink
                                        disabled={!canShowProblemsContent}
                                        onClick={onStudentNameClick}
                                        studentId={studentResult.studentId}
                                        studentName={studentResult.studentName}
                                    />
                                </TableCell>
                                <ResultsTableBlockBody
                                    canShowProblemsContent={canShowProblemsContent}
                                    classId={classId}
                                    expandProblem={expandProblem}
                                    handleMouseTableLeave={handleMouseTableLeave}
                                    onStudentNameClick={onStudentNameClick}
                                    setCellRef={setCellRef}
                                    setHoveredIndex={setHoveredIndex}
                                    shouldHideBorder={shouldHideBorder}
                                    studentResult={studentResult}
                                    style={style}
                                    tableStyle={tableStyle}
                                />
                                {shouldShowSeenColumn && (
                                    <TableCell type={TableCell.TYPE.OVERALL}>
                                        <Text>{getCellValue(studentResult.result.overall.seenPercent, intl)}</Text>
                                    </TableCell>
                                )}
                                {isPointsMode && (
                                    <TableCell type={TableCell.TYPE.OVERALL}>
                                        <Text>
                                            {isLessonScoreAvailable
                                                ? studentResult.result.overall.points
                                                : getStubValue(intl)}
                                        </Text>
                                    </TableCell>
                                )}
                                {isDefaultMode && (
                                    <TableCell type={TableCell.TYPE.OVERALL}>
                                        <Text>{getCellValue(studentResult.result.overall.correctPercent, intl)}</Text>
                                    </TableCell>
                                )}
                                {shouldShowTimeColumnStats && (
                                    <TableCell type={TableCell.TYPE.OVERALL}>
                                        <Text>
                                            {getCellValue(getFormattedTime(studentResult.result.overall.time), intl)}
                                        </Text>
                                    </TableCell>
                                )}
                                {shouldShowGameCertificate && (
                                    <TableCell type={TableCell.TYPE.CERTIFICATE}>
                                        <Link
                                            disabled={!studentResult.result.overall.time}
                                            onClick={() =>
                                                downloadGameCertificate({ studentId: studentResult.studentId })
                                            }
                                            theme={Link.THEME.INHERIT}
                                        >
                                            <Text id="lessonModal.studentCertificates.download" />
                                        </Link>
                                    </TableCell>
                                )}
                                {shouldShowCertificateColumn && (
                                    <TableCell type={TableCell.TYPE.CERTIFICATE}>
                                        {certificates?.[studentResult.studentId]?.template ? (
                                            <PdfGeneratorLink
                                                className="link link_theme_inherit"
                                                deps={[
                                                    studentResult.studentId,
                                                    certificates[studentResult.studentId].template!,
                                                ]}
                                                download={
                                                    pdfCertFilenames[studentResult.studentId] ||
                                                    buildFallbalckCertFilename(
                                                        intl,
                                                        studentResult.studentId,
                                                        certificates[studentResult.studentId].args!,
                                                        '.pdf'
                                                    )
                                                }
                                                getInputs={() => [certificates[studentResult.studentId].args!]}
                                                getTemplate={() =>
                                                    fetch(certificates[studentResult.studentId].template!).then(
                                                        (resp) => resp.json()
                                                    )
                                                }
                                                looseInputs={true}
                                                onGenerationFailure={(err) => {
                                                    onPdfGenerationError('pdfGeneration.failure', err, {
                                                        studentId: studentResult.studentId,
                                                        template: certificates[studentResult.studentId].template,
                                                        by: 'teacher',
                                                    });
                                                }}
                                            >
                                                <Text id="lessonModal.studentCertificates.download" />
                                            </PdfGeneratorLink>
                                        ) : (
                                            <Link
                                                disabled={!certificates || !certificates[studentResult.studentId]}
                                                onClick={() =>
                                                    downloadCertificate({ studentId: studentResult.studentId })
                                                }
                                                theme={Link.THEME.INHERIT}
                                            >
                                                <Text id="lessonModal.studentCertificates.download" />
                                            </Link>
                                        )}
                                    </TableCell>
                                )}
                                {shouldShowStickersColumnStats && (
                                    <TableCell type={TableCell.TYPE.STICKERS}>
                                        <ImageContextProvider value={{ resolution: SCREEN_RESOLUTIONS.X1 }}>
                                            <Button
                                                checked={
                                                    stickersPopupState.shouldShow && index === checkedStickerCellIndex
                                                }
                                                icon={
                                                    <Image
                                                        alt=""
                                                        src={getSticker(
                                                            getResourceByStudent(studentResult.studentId),
                                                            intl
                                                        )}
                                                    />
                                                }
                                                innerRef={(element) => {
                                                    studentStickerRefs.current[index] = element;
                                                }}
                                                onClick={() => {
                                                    handleStickerCellCLick(studentResult.studentId, index);
                                                }}
                                                rounded
                                                size={Button.SIZE.S}
                                                view={Button.VIEW.CLEAR}
                                            />
                                        </ImageContextProvider>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <ResultsCellDescription owner={cellRef} />
                <ResultHeaderCellDescription owner={headerCardRef} showPopup={Boolean(headerCardRef)} />
            </>
        );
    };
    /* eslint-enable complexity */

    if (
        !problemsResult ||
        lessonLoadingStatus !== LOADING_STATUSES.SUCCESS ||
        studentsResult.length === 0 ||
        students?.length === 0
    ) {
        return null;
    }

    const { lessonStatus, setShouldShowDescriptionPopup } = props;

    const handleMouseTableLeave = () => {
        setShouldShowDescriptionPopup(false);
        setHoveredIndex(null);
    };

    const shouldHideBorder = lessonStatus ? isLessonCompleted(lessonStatus) : false;
    const cellOffset = getCellsOffset();
    const style = { left: `${-cellOffset}px` };
    const tableStyle = { width: containerWidth ? `${containerWidth}px` : 'auto' };
    const shouldShowLeftScrollControl =
        scrollControlsMode === SCROLL_CONTROLS_MODE.LEFT || scrollControlsMode === SCROLL_CONTROLS_MODE.BOTH;
    const shouldShowRightScrollControl =
        scrollControlsMode === SCROLL_CONTROLS_MODE.RIGHT || scrollControlsMode === SCROLL_CONTROLS_MODE.BOTH;
    const shouldShowSeenColumn = shouldShowSeenCountColumnStats;
    const shouldShowCertificateColumn = isStatisticsAvailable && lessonHasCertificates;

    const shouldRenderDictationStats = dictationProblems.length > 0;
    const getDictationStats = () =>
        dictationProblems.map((pl) => (
            <DictationResult
                key={pl.id}
                onStudentNameClick={onStudentNameClick}
                problemLink={pl}
                shouldShowMistakes={isEvaluated}
            />
        ));
    const shouldRenderProblemsStats = problems.length > 0;

    return (
        <div className={cx(b('results', { expanded: isStatsExpanded }), mix)} ref={resultRef}>
            <div className="results__stats-wrapper">
                {shouldRenderDictationStats ? getDictationStats() : null}
                {shouldRenderProblemsStats ? renderProblemStats() : null}
                {shouldRenderStickersMenu && stickersPopupState.anchorRef && (
                    <ImagesMenu
                        anchorRef={stickersPopupState.anchorRef}
                        directions={stickersPopupState.directions}
                        mainOffset={stickersPopupState.mainOffset}
                        menuItems={clessonMetaStickers}
                        mix="qa_stickers-menu"
                        onChange={stickersPopupState.onChange}
                        onClose={closeStickersPopup}
                        visible={stickersPopupState.shouldShow}
                    />
                )}
            </div>
        </div>
    );
};

type StoreType = ClassesState & LessonStore & CertificatesStore & ReduxUserStore & ReduxStickersStore;

const mapStateToProps = <T extends StoreType>(storeState: T, { now }: HOCProps) => {
    const courseId = lessonSelectors.getLessonCourseId(storeState);
    const classId = getTeamIdFromPath();
    const { studentsResult } = selectors.getResultsViewProps(storeState, { classId });

    return {
        classId,
        lessonLoadingStatus: lessonSelectors.getLessonLoadingStatus(storeState),
        canShowProblemsContent: lessonSelectors.getCanShowProblemsContent(storeState),
        clessonId: lessonSelectors.getCLessonId(storeState),
        isEvaluable: lessonSelectors.getIsLessonEvaluable(storeState),
        lessonStatus: lessonSelectors.getLessonStatus(storeState),
        problems: selectors.getCommonProblems(storeState),
        dictationProblems: selectors.getDictationProblems(storeState),
        problemsResult: selectors.getProblemsResult(storeState),
        isPointsMode: lessonSelectors.getIsPointsMode(storeState),
        isDefaultMode: lessonSelectors.getIsDefaultMode(storeState),
        isLessonScoreAvailable: lessonSelectors.getIsLessonScoreAvailable(storeState),
        studentsResult,
        maxPoints: selectors.getMaxPoints(storeState, { classId }),
        students: getStudents(storeState, { classId }),
        certificates: selectors.getCertificatesByStudentId(storeState, { courseId }),
        isStatisticsAvailable: lessonSelectors.getIsStatisticsAvailable(storeState, { now }),
        lessonHasCertificates: lessonSelectors.getCanCertificateBeIssuedForLesson(storeState),
        isEvaluated: lessonSelectors.getIsLessonEvaluated(storeState),
        isStatsExpanded: selectors.getIsStatsExpanded(storeState),
        shouldShowSeenCountColumnStats: lessonSelectors.getLessonResultShowSeenCountColumnStats(storeState),
        shouldShowTimeColumnStats: lessonSelectors.getLessonResultShowTimeColumnStats(storeState),
        clessonMetaStickers: getClessonMetaStickers(storeState),
        clessonStudentsStickers: getClessonStudentsStickers(storeState),
        isClessonMetaHasStickers: lessonSelectors.getIsClessonMetaHasStickers(storeState),
        stickerResources: getStickerResources(storeState),
        stickersConfig: getStickersConfig(storeState),
        studentIdsWithoutResult: selectors.getStudentIdsWithoutStickers(storeState, { classId }),
        gameCertSubject: lessonSelectors.getGameCertificateSubject(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) =>
    bindActionCreators(
        {
            setSelectedProblemId: problemsViewActions.setSelectedProblemId,
            setShouldShowDescriptionPopup: resultsActions.setShouldShowDescriptionPopup,
            setDescriptionPopupData: resultsActions.setDescriptionPopupData,
            setShouldShowAttempts: resultsActions.setShouldShowAttempts,
            setShouldShowTime: resultsActions.setShouldShowTime,
            setShouldShowPoints: resultsActions.setShouldShowPoints,
            expandProblem: lessonContentActions.expandProblem,
        },
        dispatch
    );

export default injectIntl(withRouterPath(withTime(connect(mapStateToProps, mapDispatchToProps)(Results))));
